/**
 * Sneak-peek logic for Free users (Manual Unlock).
 *
 * Al mount chiama soltanto GET /api/preview/match-status/{fid} che NON
 * incrementa nulla. Espone una funzione `unlock()` che effettua il
 * POST /api/preview/use-match SOLO quando l'utente clicca esplicitamente
 * sul bottone CTA in MatchPage — evita che lo slot gratuito venga sprecato
 * aprendo una partita per errore.
 *
 * Premium users vedono `canView=true, isPremium=true` immediatamente.
 *
 * Returns:
 *   {
 *     canView,         // true → render contenuto unlocked
 *     isPremium,
 *     used, limit, remaining,
 *     alreadyViewed,   // partita già sbloccata in passato
 *     canUnlock,       // ha ancora credito sneak peek disponibile
 *     loading,
 *     unlocking,       // true durante la chiamata POST
 *     unlock,          // function: chiamare onClick
 *     unlockError,
 *   }
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useMatchPreview(fixtureId) {
  const { getToken, isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!fixtureId || !isAuthenticated) {
      setData(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    const token = getToken();
    if (!token) { setLoading(false); return () => { cancelled = true; }; }
    axios
      .get(`${API}/preview/match-status/${encodeURIComponent(String(fixtureId))}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fixtureId, isAuthenticated, getToken]);

  const unlock = useCallback(async () => {
    if (!fixtureId || !isAuthenticated) return null;
    const token = getToken();
    if (!token) return null;
    setUnlocking(true);
    setUnlockError(null);
    try {
      const res = await axios.post(
        `${API}/preview/use-match`,
        { fixture_id: String(fixtureId) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData({
        ...res.data,
        already_viewed: true,
        can_unlock: false,
      });
      return res.data;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Errore sblocco partita";
      setUnlockError(msg);
      return null;
    } finally {
      setUnlocking(false);
    }
  }, [fixtureId, isAuthenticated, getToken]);

  return {
    canView: !!data?.can_view,
    isPremium: !!data?.is_premium,
    used: data?.preview_used ?? 0,
    limit: data?.preview_limit ?? null,
    remaining: data?.preview_remaining ?? null,
    alreadyViewed: !!data?.already_viewed,
    canUnlock: !!data?.can_unlock,
    loading,
    unlocking,
    unlock,
    unlockError,
  };
}
